/** XeAgents.ai **/
import { useCallback, useEffect, useRef, useState } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useQuery } from "@tanstack/react-query";

const libraries: ("places")[] = ["places"];

interface AddressComponents {
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

export function useGooglePlacesAutocomplete(
  inputRef: React.RefObject<HTMLInputElement>,
  onAddressSelected: (address: AddressComponents) => void
) {
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  
  // Fetch Google API key from settings
  const { data: apiKeyData } = useQuery<{ google_api_key: string | null }>({
    queryKey: ["/api/settings/google-api-key"],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const googleApiKey = apiKeyData?.google_api_key || null;
  
  // Only attempt to load the script if we have an API key
  const shouldLoad = !!googleApiKey;
  
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleApiKey || "",
    libraries,
    preventGoogleFontsLoading: true,
  });

  useEffect(() => {
    // Don't initialize if no API key is available
    if (!shouldLoad || !isLoaded || !inputRef.current || autocomplete) return;

    const autocompleteInstance = new google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      fields: ["address_components", "formatted_address"],
    });

    autocompleteInstance.addListener("place_changed", () => {
      const place = autocompleteInstance.getPlace();
      
      if (!place.address_components) {
        return;
      }

      const addressComponents: AddressComponents = {
        street: "",
        city: "",
        region: "",
        postalCode: "",
        country: "",
      };

      let streetNumber = "";
      let route = "";

      for (const component of place.address_components) {
        const types = component.types;

        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (types.includes("route")) {
          route = component.long_name;
        }
        if (types.includes("locality")) {
          addressComponents.city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          addressComponents.region = component.short_name;
        }
        if (types.includes("postal_code")) {
          addressComponents.postalCode = component.long_name;
        }
        if (types.includes("country")) {
          addressComponents.country = component.short_name;
        }
      }

      // Combine street number and route
      addressComponents.street = [streetNumber, route].filter(Boolean).join(" ");

      onAddressSelected(addressComponents);
    });

    setAutocomplete(autocompleteInstance);
  }, [isLoaded, inputRef, onAddressSelected, autocomplete]);

  return {
    isLoaded: shouldLoad && isLoaded,
    loadError: !shouldLoad ? new Error("Google API key not configured") : loadError,
  };
}